import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Flag } from "lucide-react";
import { API, errMsg, TOPIC_NAMES } from "../lib/api";
import { PageHeader, EmptyState } from "../components/bits";
import { toast } from "sonner";

export default function Guides() {
  const navigate = useNavigate();
  const [guides, setGuides] = useState(null);

  useEffect(() => {
    API.get("/guides").then(({ data }) => setGuides(data))
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const byTopic = {};
  (guides || []).forEach((g) => {
    (byTopic[g.topic] = byTopic[g.topic] || []).push(g);
  });
  const topicsWithGuides = Object.keys(TOPIC_NAMES).filter((t) => byTopic[t]?.length);

  return (
    <div data-testid="guides-page" className="max-w-5xl">
      <PageHeader title="Revision guides"
        sub="Concise clinical revision notes, organised by topic. Flag any guide to find it quickly in your Review section." />
      {guides === null ? (
        <p className="py-16 text-center text-sm text-muted-foreground">Loading guides…</p>
      ) : topicsWithGuides.length === 0 ? (
        <EmptyState title="No guides published yet"
          body="Revision guides will appear here as soon as they are published by our clinical team."
          testid="guides-empty" />
      ) : (
        topicsWithGuides.map((t) => (
          <section key={t} className="mb-10" data-testid={`guides-topic-${t}`}>
            <h2 className="font-display text-lg font-bold mb-4 uppercase tracking-wide border-b border-border pb-2">
              {TOPIC_NAMES[t]}
              <span className="ml-2 text-sm font-normal text-muted-foreground normal-case">
                {byTopic[t].length} guide{byTopic[t].length === 1 ? "" : "s"}
              </span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {byTopic[t].map((g) => (
                <button key={g.id} data-testid={`guide-card-${g.id}`} onClick={() => navigate(`/app/guides/${g.id}`)}
                  className="text-left bg-card border border-border rounded-lg p-5 hover:shadow-md hover:border-primary/40 transition-all duration-200">
                  <div className="flex items-start justify-between gap-2">
                    <BookOpen className="w-5 h-5 text-primary shrink-0" />
                    {g.flagged && <Flag className="w-4 h-4 text-accent fill-accent" data-testid={`guide-flagged-icon-${g.id}`} />}
                  </div>
                  <h3 className="mt-3 font-display font-semibold leading-snug">{g.title}</h3>
                  <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2 leading-relaxed">{g.excerpt}</p>
                </button>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
